import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Empty,
  Form,
  List,
  Modal,
  Select,
  Typography,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { DefaultOptionType } from 'antd/lib/select';
import { validateDateNotInPast } from 'client/utils/formValidations';
import useFuseSearch from '../../hooks/useFuseSearch';
import { useGetPackagesQuery } from '../../../trial-package/services/hooks/useGetPackagesQuery';

type View = 'choice' | 'cancel' | 'reenable';

type Props = {
  open: boolean;
  isLoading: boolean;
  isCancelling: boolean;
  onSubmit: (
    packageId: number,
    packageStart: string,
    packageEnd: string
  ) => void;
  onCancelSubscription: () => void;
  onCancel: () => void;
};

type UnpauseForm = {
  packageID: number;
  packageStart: moment.Moment;
  packageEnd: moment.Moment;
};

const CANCEL_EFFECTS = [
  'All users on the team will remain set to expired.',
  'All user accounts will be scheduled for deletion in 30 days.',
];

export const UnpauseSubscriptionModal = ({
  open,
  isLoading,
  isCancelling,
  onSubmit,
  onCancelSubscription,
  onCancel,
}: Props): ReactElement => {
  const [view, setView] = useState<View>('choice');
  const [form] = Form.useForm<UnpauseForm>();

  const { data: packagesData } = useGetPackagesQuery({
    enabled: true,
    params: {},
  });

  const packageOptions = useMemo<Array<DefaultOptionType>>(
    () =>
      packagesData?.packages.map((p) => ({
        label: p.name,
        value: p.id_package,
      })) ?? [],
    [packagesData]
  );

  const { setSearchQuery, results: filteredResults } = useFuseSearch({
    data: packageOptions,
    options: { keys: ['label'], shouldSort: true, threshold: 0.3 },
  });

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setView('choice');
    }
  }, [open]);

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        onSubmit(
          values.packageID,
          moment(values.packageStart).format('YYYY-MM-DD HH:mm:ss'),
          moment(values.packageEnd).format('YYYY-MM-DD HH:mm:ss')
        );
      })
      .catch(() => {});
  };

  const selectedPackageId = Form.useWatch('packageID', form);
  const selectedPackage = packageOptions.find(
    (p) => p.value === selectedPackageId
  );
  const packageStart = Form.useWatch('packageStart', form);
  const packageEnd = Form.useWatch('packageEnd', form);

  const unpauseEffects = [
    `Team will be moved off the Expired package${
      selectedPackage ? ` and placed on "${selectedPackage.label}"` : ''
    }.`,
    `Package dates will be set to ${
      packageStart ? moment(packageStart).format('MM/DD/YYYY') : '—'
    } – ${packageEnd ? moment(packageEnd).format('MM/DD/YYYY') : '—'}.`,
    'All users will be moved off expired onto the specified package and date range.',
  ];

  const modalTitle: Record<View, ReactNode> = {
    choice: 'Unpause Subscription',
    cancel: (
      <span>
        <ExclamationCircleOutlined className="text-red-500 mr-2" />
        Cancel Subscription
      </span>
    ),
    reenable: 'Re-enable Subscription',
  };

  const modalFooter: Record<View, ReactNode> = {
    choice: (
      <>
        <Button onClick={onCancel}>Close</Button>
        <Button danger onClick={() => setView('cancel')}>
          Cancel Subscription
        </Button>
        <Button type="primary" onClick={() => setView('reenable')}>
          Re-enable Subscription
        </Button>
      </>
    ),
    cancel: (
      <>
        <Button onClick={() => setView('choice')} disabled={isCancelling}>
          ← Back
        </Button>
        <Button
          danger
          type="primary"
          loading={isCancelling}
          disabled={isCancelling}
          onClick={onCancelSubscription}
        >
          Confirm Cancel
        </Button>
      </>
    ),
    reenable: (
      <>
        <Button onClick={() => setView('choice')} disabled={isLoading}>
          ← Back
        </Button>
        <Button
          type="primary"
          loading={isLoading}
          disabled={isLoading}
          onClick={handleOk}
        >
          Confirm Unpause
        </Button>
      </>
    ),
  };

  return (
    <Modal
      title={modalTitle[view]}
      centered
      open={open}
      footer={modalFooter[view]}
      onCancel={onCancel}
      closable={false}
    >
      {view === 'choice' && (
        <Typography.Paragraph>
          This team&apos;s subscription is currently paused. How would you like
          to proceed?
        </Typography.Paragraph>
      )}

      {view === 'cancel' && (
        <>
          <Typography.Paragraph>
            You are about to <strong>cancel</strong> this team&apos;s
            subscription. The following will apply:
          </Typography.Paragraph>
          <List
            dataSource={CANCEL_EFFECTS}
            renderItem={(item) => (
              <List.Item className="!px-0">
                <Typography.Text>{item}</Typography.Text>
              </List.Item>
            )}
          />
        </>
      )}

      {view === 'reenable' && (
        <>
          <Form layout="vertical" form={form} style={{ maxWidth: 600 }}>
            <Form.Item
              name="packageID"
              label="Package Type"
              rules={[{ required: true, message: 'Please select a package' }]}
            >
              <Select
                showSearch
                placeholder="Select a Package"
                options={filteredResults}
                notFoundContent={<Empty />}
                onSearch={setSearchQuery}
                filterOption={false}
              />
            </Form.Item>
            <Form.Item
              name="packageStart"
              label="Start Date"
              rules={[
                { required: true, message: 'Please select a start date' },
                { validator: validateDateNotInPast(form, 'packageStart') },
              ]}
              validateTrigger="onChange"
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="packageEnd"
              label="End Date"
              rules={[
                { required: true, message: 'Please select an end date' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (
                      !value ||
                      getFieldValue('packageStart')?.isBefore(value)
                    ) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error('End date must be after start date')
                    );
                  },
                }),
              ]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Form>

          <Typography.Title level={5} className="!font-medium mt-2">
            Summary of changes
          </Typography.Title>
          <List
            dataSource={unpauseEffects}
            renderItem={(item) => (
              <List.Item className="!px-0">
                <Typography.Text>{item}</Typography.Text>
              </List.Item>
            )}
          />
          <Alert
            type="info"
            showIcon
            message="These changes will propagate down to all users on the team."
            className="mt-2"
          />
        </>
      )}
    </Modal>
  );
};

export default UnpauseSubscriptionModal;
